"use client";

import { c as _c } from "react/compiler-runtime";
import { Check, X } from "lucide-react";
interface CompetitorComparisonProps {
  locale: string;
}
type CellValue = true | false | "partial" | string;
interface FeatureRow {
  feature: {
    fr: string;
    en: string;
  };
  velyq: CellValue;
  huntr: CellValue;
  simplify: CellValue;
  linkedinPremium: CellValue;
  huru: CellValue;
  note?: {
    fr: string;
    en: string;
  };
}
const ROWS: FeatureRow[] = [{
  feature: {
    fr: "Analyse ATS du CV",
    en: "Resume ATS analysis"
  },
  velyq: true,
  huntr: "partial",
  simplify: "partial",
  linkedinPremium: false,
  huru: false
}, {
  feature: {
    fr: "CV Builder IA",
    en: "AI CV Builder"
  },
  velyq: true,
  huntr: true,
  simplify: true,
  linkedinPremium: false,
  huru: false
}, {
  feature: {
    fr: "Simulation d'entretien IA",
    en: "AI interview simulation"
  },
  velyq: true,
  huntr: false,
  simplify: false,
  linkedinPremium: false,
  huru: true
}, {
  feature: {
    fr: "Simulation vocale (live coach)",
    en: "Live voice simulation"
  },
  velyq: true,
  huntr: false,
  simplify: false,
  linkedinPremium: false,
  huru: "partial"
}, {
  feature: {
    fr: "Modes sectoriels (tech, finance, consulting)",
    en: "Sector modes (tech, finance, consulting)"
  },
  velyq: true,
  huntr: false,
  simplify: false,
  linkedinPremium: false,
  huru: false
}, {
  feature: {
    fr: "Lettres de motivation IA",
    en: "AI cover letters"
  },
  velyq: true,
  huntr: true,
  simplify: true,
  linkedinPremium: false,
  huru: false
}, {
  feature: {
    fr: "LinkedIn rewrite (headline + résumé)",
    en: "LinkedIn rewrite (headline + summary)"
  },
  velyq: true,
  huntr: false,
  simplify: false,
  linkedinPremium: "partial",
  huru: false
}, {
  feature: {
    fr: "Tracker de candidatures",
    en: "Application tracker"
  },
  velyq: true,
  huntr: true,
  simplify: true,
  linkedinPremium: false,
  huru: false
}, {
  feature: {
    fr: "Extension Chrome (clip LinkedIn/Indeed)",
    en: "Chrome extension (clip LinkedIn/Indeed)"
  },
  velyq: true,
  huntr: true,
  simplify: true,
  linkedinPremium: false,
  huru: false
}, {
  feature: {
    fr: "FR natif (UI + coach IA + templates)",
    en: "FR native (UI + AI coach + templates)"
  },
  velyq: true,
  huntr: false,
  simplify: false,
  linkedinPremium: "partial",
  huru: true
}, {
  feature: {
    fr: "Hébergement UE (RGPD)",
    en: "EU hosting (GDPR)"
  },
  velyq: true,
  huntr: false,
  simplify: false,
  linkedinPremium: false,
  huru: "partial"
}, {
  feature: {
    fr: "Plan gratuit utilisable",
    en: "Usable free plan"
  },
  velyq: true,
  huntr: "partial",
  simplify: true,
  linkedinPremium: false,
  huru: false
}, {
  feature: {
    fr: "Prix Premium (€/mois)",
    en: "Premium price (€/month)"
  },
  velyq: "9,99 €",
  huntr: "39 $",
  simplify: "30 $",
  linkedinPremium: "29,99 €",
  huru: "24,99 €"
}];
type ColHeader = {
  key: "velyq" | "huntr" | "simplify" | "linkedinPremium" | "huru";
  label: string;
  highlight?: boolean;
};
const COL_HEADERS: ColHeader[] = [{
  key: "velyq",
  label: "Velyq",
  highlight: true
}, {
  key: "huntr",
  label: "Huntr"
}, {
  key: "simplify",
  label: "Simplify"
}, {
  key: "linkedinPremium",
  label: "LinkedIn Premium"
}, {
  key: "huru",
  label: "Huru"
}];
function Cell(t0) {
  const $ = _c(15);
  const {
    value,
    isVelyq,
    locale
  } = t0;
  const isFr = locale === "fr";
  if (value === true) {
    const t1 = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${isVelyq ? "bg-primary/15 text-primary" : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"}`;
    const t2 = isFr ? "Oui" : "Yes";
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Check className="h-3 w-3" />;
      $[0] = t3;
    } else {
      t3 = $[0];
    }
    const t4 = isFr ? "Oui" : "Yes";
    let t5;
    if ($[1] !== t1 || $[2] !== t2 || $[3] !== t4) {
      t5 = <span className={t1} aria-label={t2}>{t3}{t4}</span>;
      $[1] = t1;
      $[2] = t2;
      $[3] = t4;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    return t5;
  }
  if (value === false) {
    const t1 = isFr ? "Non" : "No";
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <X className="h-3 w-3" />;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const t3 = isFr ? "Non" : "No";
    let t4;
    if ($[6] !== t1 || $[7] !== t3) {
      t4 = <span className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground" aria-label={t1}>{t2}{t3}</span>;
      $[6] = t1;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (value === "partial") {
    const t1 = isFr ? "Partiel" : "Partial";
    const t2 = isFr ? "Partiel" : "Partial";
    let t3;
    if ($[9] !== t1 || $[10] !== t2) {
      t3 = <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400" aria-label={t1}>{t2}</span>;
      $[9] = t1;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  const t1 = `text-[11px] font-semibold ${isVelyq ? "text-primary" : "text-foreground"}`;
  let t2;
  if ($[12] !== t1 || $[13] !== value) {
    t2 = <span className={t1}>{value}</span>;
    $[12] = t1;
    $[13] = value;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
export function CompetitorComparison(t0) {
  const $ = _c(29);
  const {
    locale
  } = t0;
  const isFr = locale === "fr";
  const t1 = isFr ? "COMPARATIF HONN\xCATE" : "HONEST COMPARISON";
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-semibold tracking-wider text-primary">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = isFr ? "Velyq vs. Huntr, Simplify, LinkedIn Premium, Huru" : "Velyq vs. Huntr, Simplify, LinkedIn Premium, Huru";
  let t4;
  if ($[2] !== t3) {
    t4 = <h2 id="competitor-comparison-heading" className="mt-4 text-2xl font-bold sm:text-3xl">{t3}</h2>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = isFr ? "Chaque ligne = une feature. On dit o\xF9 on ne gagne pas. Sources : pages pricing publiques 2026, docs produit, extensions Chrome Web Store." : "One row = one feature. We say where we don't win. Sources: public 2026 pricing pages, product docs, Chrome Web Store listings.";
  let t6;
  if ($[4] !== t5) {
    t6 = <p className="mt-2 text-sm text-muted-foreground max-w-2xl mx-auto">{t5}</p>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t2 || $[7] !== t4 || $[8] !== t6) {
    t7 = <div className="mb-10 text-center">{t2}{t4}{t6}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = isFr ? "Feature" : "Feature";
  let t9;
  if ($[10] !== t8) {
    t9 = <th className="p-3 text-left font-semibold text-foreground">{t8}</th>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = COL_HEADERS.map(_temp);
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t9) {
    t11 = <thead className="bg-muted/30"><tr>{t9}{t10}</tr></thead>;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== isFr || $[16] !== locale) {
    t12 = ROWS.map(row => <tr key={row.feature.fr} className="border-t hover:bg-muted/20 transition-colors"><td className="p-3 text-muted-foreground">{isFr ? row.feature.fr : row.feature.en}</td>{COL_HEADERS.map(h_0 => {
        const val = row[h_0.key as keyof FeatureRow] as CellValue;
        return <td key={h_0.key} className={`p-3 text-center ${h_0.highlight ? "bg-primary/5" : ""}`}><Cell value={val} isVelyq={h_0.highlight === true} locale={locale} /></td>;
      })}</tr>);
    $[15] = isFr;
    $[16] = locale;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12) {
    t13 = <tbody>{t12}</tbody>;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t11 || $[21] !== t13) {
    t14 = <div className="overflow-x-auto rounded-xl border bg-card"><table className="w-full min-w-[720px] text-sm">{t11}{t13}</table></div>;
    $[20] = t11;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = isFr ? "Mise \xE0 jour : avril 2026. Pricing alternatifs convertis au cours USD\u2192EUR du jour. Tu vois une erreur ? contact@velyq.com." : "Last updated: April 2026. Alternative pricing converted at current USD\u2192EUR rate. Spot a mistake? contact@velyq.com.";
  let t16;
  if ($[23] !== t15) {
    t16 = <p className="mt-4 text-center text-xs text-muted-foreground">{t15}</p>;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t14 || $[26] !== t16 || $[27] !== t7) {
    t17 = <section aria-labelledby="competitor-comparison-heading" className="border-t py-16 sm:py-20"><div className="container mx-auto px-4"><div className="mx-auto max-w-5xl">{t7}{t14}{t16}</div></div></section>;
    $[25] = t14;
    $[26] = t16;
    $[27] = t7;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  return t17;
}
function _temp(h) {
  return <th key={h.key} className={`p-3 text-center font-semibold ${h.highlight ? "bg-primary/5 text-primary" : "text-foreground"}`}>{h.label}</th>;
}